import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Tabs,
  Tab,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  Timeline as TimelineIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Percent as PercentIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  DateRange as DateRangeIcon,
  LocationOn as LocationOnIcon,
  AttachMoney as CapitalIcon,
  TrendingUp as TrendingUpIcon
} from "@mui/icons-material";
import { format } from "date-fns";
import { GetDataFromApiWithParams } from "../../../../services/api/ExecuteApiRequests"

// Helper function to safely parse number
const safeParseNumber = (value) => {
  if (!value && value !== 0) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const cleanValue = value.replace(/,/g, '').trim();
    const parsed = parseFloat(cleanValue);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

// Format number with commas
const formatNumber = (number) => {
  if (!number && number !== 0) return "0";
  return new Intl.NumberFormat('en-US').format(number);
};

// Format percentage
const formatPercent = (value) => {
  if (!value && value !== 0) return "0%";
  return `${Math.round(value)}%`;
};

// Format currency
const formatCurrency = (value) => {
  if (!value && value !== 0) return "$0";
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
};

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`investor-tabpanel-${index}`}
      aria-labelledby={`investor-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Bar Chart Component
const BarChartComponent = ({ title, data, dataKey, color, formatValue, height = 400 }) => {
  const items = Object.keys(data);
  
  if (items.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available</Typography>
      </Box>
    );
  }
  
  const maxValue = Math.max(...Object.values(data).map(d => d[dataKey] || 0), 0);
  
  if (maxValue === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available for {title}</Typography>
      </Box>
    );
  }
  
  const barWidth = Math.min(50, Math.max(30, 80 - items.length * 2));
  
  return (
    <Box sx={{ p: 2, overflowX: 'auto' }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E', mb: 3, textAlign: 'center' }}>
        {title}
      </Typography>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-end', 
        gap: 2.5, 
        minHeight: height, 
        justifyContent: 'center', 
        flexWrap: 'wrap',
        padding: 2
      }}>
        {items.map((item) => {
          const value = data[item][dataKey] || 0;
          const chartHeight = height - 100;
          const barHeight = maxValue > 0 ? (value / maxValue) * chartHeight : 0;
          const percentage = maxValue > 0 ? ((value / maxValue) * 100).toFixed(1) : 0;
          
          return (
            <Tooltip 
              key={item} 
              title={`${item}: ${formatValue(value)} (${percentage}% of max)`} 
              arrow 
              placement="top"
            >
              <Box sx={{ 
                textAlign: 'center', 
                minWidth: 70,
                maxWidth: 120,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-5px)'
                }
              }}>
                <Box sx={{ 
                  position: 'relative',
                  height: barHeight,
                  width: `${barWidth}px`,
                  bgcolor: color,
                  borderRadius: '4px 4px 0 0',
                  margin: '0 auto',
                  transition: 'all 0.3s ease',
                  opacity: 0.85,
                  '&:hover': {
                    opacity: 1,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                  }
                }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      position: 'absolute', 
                      top: -22, 
                      left: '50%', 
                      transform: 'translateX(-50%)',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      color: color,
                      whiteSpace: 'nowrap',
                      display: barHeight > 35 ? 'none' : 'block'
                    }}
                  >
                    {formatValue(value)}
                  </Typography>
                </Box>
                
                {barHeight > 35 && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      color: 'white',
                      mt: -2.5,
                      mb: 0.5,
                      position: 'relative',
                      zIndex: 1,
                      textShadow: '0px 0px 2px rgba(0,0,0,0.5)'
                    }}
                  >
                    {formatValue(value)}
                  </Typography>
                )}
                
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mt: 1.5, 
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    maxWidth: '100px',
                    wordWrap: 'break-word',
                    lineHeight: 1.2,
                    color: '#333'
                  }}
                >
                  {item.length > 20 ? item.substring(0, 18) + '...' : item}
                </Typography>
                
                <Typography 
                  variant="caption" 
                  sx={{ 
                    display: 'block', 
                    color: '#888',
                    fontSize: '0.65rem',
                    mt: 0.25
                  }}
                >
                  ({percentage}%)
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      
      <Box sx={{ 
        width: '100%', 
        bgcolor: '#f8f9fa', 
        mt: 3, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        p: 2, 
        borderRadius: 2,
        border: '1px solid #e0e0e0'
      }}>
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
          Total {title}: {formatValue(Object.values(data).reduce((sum, d) => sum + (d[dataKey] || 0), 0))}
        </Typography>
      </Box>
    </Box>
  );
};

// Pie Chart Component
const PieChartComponent = ({ title, data, formatValue }) => {
  const items = Object.keys(data);
  const total = items.reduce((sum, key) => sum + (data[key] || 0), 0);
  const colors = ['#4caf50', '#ff9800', '#e91e63', '#2196f3', '#9c27b0', '#d32f2f', '#0288d1', '#7b1fa2', '#e65100', '#c2185b', '#00695c', '#f44336'];
  
  if (items.length === 0 || total === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No data available for the selected filters</Typography>
      </Box>
    );
  }
  
  let startAngle = 0;
  const centerX = 120, centerY = 120, radius = 90;
  const svgSize = 280;
  
  const getPercentage = (value) => {
    return total > 0 ? ((value / total) * 100).toFixed(1) : 0;
  };
  
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E', mb: 2, textAlign: 'center' }}>
        {title} - Total: {formatValue(total)}
      </Typography>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', alignItems: 'center' }}>
        <Box sx={{ position: 'relative', width: svgSize, height: svgSize }}>
          <svg width={svgSize} height={svgSize} viewBox={`0 0 ${svgSize} ${svgSize}`}>
            {items.map((key, index) => {
              const value = data[key] || 0;
              const percentage = total > 0 ? (value / total) * 100 : 0;
              const angle = (percentage / 100) * 360;
              const endAngle = startAngle + angle;
              
              const startRad = (startAngle * Math.PI) / 180;
              const endRad = (endAngle * Math.PI) / 180;
              
              const x1 = centerX + radius * Math.cos(startRad);
              const y1 = centerY + radius * Math.sin(startRad);
              const x2 = centerX + radius * Math.cos(endRad);
              const y2 = centerY + radius * Math.sin(endRad);
              
              const largeArcFlag = angle > 180 ? 1 : 0;
              const pathData = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
              
              const slice = (
                <path 
                  key={key} 
                  d={pathData} 
                  fill={colors[index % colors.length]} 
                  stroke="white" 
                  strokeWidth="2"
                  style={{ cursor: 'pointer', transition: 'opacity 0.3s' }}
                  onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                  onMouseLeave={(e) => e.target.style.opacity = '1'}
                />
              );
              startAngle = endAngle;
              return slice;
            })}
            
            <circle cx={centerX} cy={centerY} r="25" fill="white" />
            <text x={centerX} y={centerY - 5} textAnchor="middle" fontSize="11" fontWeight="bold" fill="#333">Total</text>
            <text x={centerX} y={centerY + 12} textAnchor="middle" fontSize="12" fontWeight="bold" fill="#1976d2">{formatValue(total)}</text>
          </svg>
        </Box>
        
        <Box sx={{ minWidth: 200, maxHeight: 250, overflowY: 'auto' }}>
          {items.map((key, index) => {
            const value = data[key] || 0;
            const percentage = getPercentage(value);
            return (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ width: 12, height: 12, bgcolor: colors[index % colors.length], borderRadius: 1 }} />
                <Typography variant="caption" sx={{ flex: 1 }}>
                  {key.length > 20 ? key.substring(0, 18) + '...' : key} ({percentage}%)
                </Typography>
                <Typography variant="caption" fontWeight="bold" sx={{ color: colors[index % colors.length] }}>
                  {formatValue(value)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
      
      <Box sx={{ width: '100%', bgcolor: '#f5f5f5', mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, borderRadius: 1 }}>
        <Typography variant="body2" color="textSecondary">Total Items: {items.length}</Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>Total Value: {formatValue(total)}</Typography>
      </Box>
    </Box>
  );
};

// Yearly Trend Line Chart
const YearlyTrendLineChart = ({ title, yearlyData, dataKeys, colors, formatValue }) => {
  const years = Object.keys(yearlyData).sort((a, b) => a - b);
  
  if (years.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">No yearly data available</Typography>
      </Box>
    );
  }
  
  const maxValue = Math.max(...Object.values(yearlyData).flatMap(d => 
    dataKeys.map(key => d[key] || 0)
  ), 0);
  
  const chartHeight = 350;
  const chartWidth = Math.max(600, years.length * 100);
  const padding = { top: 30, right: 40, bottom: 60, left: 80 };
  
  return (
    <Box sx={{ p: 2, overflowX: 'auto' }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E', mb: 3, textAlign: 'center' }}>
        {title}
      </Typography>
      
      <svg width={chartWidth} height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="xMidYMid meet">
        {[0, 25, 50, 75, 100].map((percent) => {
          const y = chartHeight - padding.bottom - (percent / 100) * (chartHeight - padding.top - padding.bottom);
          const value = (maxValue * percent) / 100;
          return (
            <g key={percent}>
              <line x1={padding.left} y1={y} x2={chartWidth - padding.right} y2={y} stroke="#e0e0e0" strokeWidth="1" strokeDasharray="4" />
              <text x={padding.left - 8} y={y} textAnchor="end" fontSize="10" fill="#666">{formatValue(value)}</text>
            </g>
          );
        })}
        
        {dataKeys.map((dataKey, idx) => (
          <polyline
            key={dataKey}
            points={years.map((year, index) => {
              const x = padding.left + (index / (years.length - 1 || 1)) * (chartWidth - padding.left - padding.right);
              const value = yearlyData[year]?.[dataKey] || 0;
              const y = chartHeight - padding.bottom - (value / maxValue) * (chartHeight - padding.top - padding.bottom);
              return `${x},${y}`;
            }).join(' ')}
            fill="none"
            stroke={colors[idx % colors.length]}
            strokeWidth="2.5"
          />
        ))}
        
        {years.map((year, index) => {
          const x = padding.left + (index / (years.length - 1 || 1)) * (chartWidth - padding.left - padding.right);
          
          return (
            <g key={year}>
              {dataKeys.map((dataKey, idx) => {
                const value = yearlyData[year]?.[dataKey] || 0;
                const y = chartHeight - padding.bottom - (value / maxValue) * (chartHeight - padding.top - padding.bottom);
                const color = colors[idx % colors.length];
                
                return (
                  <g key={dataKey}>
                    <circle cx={x} cy={y} r="5" fill={color} stroke="white" strokeWidth="2" style={{ cursor: 'pointer' }} />
                    {value > 0 && (
                      <text 
                        x={x} 
                        y={idx === 0 ? y - 10 : y + 15} 
                        textAnchor="middle" 
                        fontSize="8" 
                        fill={color}
                      >
                        {formatValue(value)}
                      </text>
                    )}
                  </g>
                );
              })}
              <text x={x} y={chartHeight - padding.bottom + 20} textAnchor="middle" fontSize="11" fontWeight="bold">{year}</text>
            </g>
          );
        })}
        
        <line x1={padding.left} y1={padding.top} x2={padding.left} y2={chartHeight - padding.bottom} stroke="#999" strokeWidth="1.5" />
        <line x1={padding.left} y1={chartHeight - padding.bottom} x2={chartWidth - padding.right} y2={chartHeight - padding.bottom} stroke="#999" strokeWidth="1.5" />
        
        <text x={20} y={chartHeight / 2} textAnchor="middle" fontSize="11" fill="#666" transform={`rotate(-90, 20, ${chartHeight / 2})`}>Number of Investors</text>
        <text x={chartWidth / 2} y={chartHeight - 10} textAnchor="middle" fontSize="11" fill="#666">Year</text>
      </svg>
      
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2, flexWrap: 'wrap' }}>
        {dataKeys.map((dataKey, idx) => {
          let label = "";
          if (dataKey === "totalPotential") label = "Potential Investors";
          else if (dataKey === "totalConverted") label = "Converted Investors";
          else if (dataKey === "conversionRate") label = "Conversion Rate %";
          else label = dataKey;
          
          return (
            <Box key={dataKey} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 20, height: 3, bgcolor: colors[idx % colors.length], borderRadius: 1 }} />
              <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{label}</Typography>
            </Box>
          );
        })}
      </Box>
      
      <Box sx={{ width: '100%', bgcolor: '#f5f5f5', mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2, borderRadius: 1 }}>
        <Typography variant="body2" color="textSecondary">Years: {years.join(' → ')}</Typography>
      </Box>
    </Box>
  );
};

// Gauge Chart for Conversion Rate
const ConversionGaugeChart = ({ conversionRate, potentialCount, investorCount }) => {
  const percentage = Math.min(100, Math.max(0, conversionRate));
  const angle = (percentage / 100) * 180;
  const radius = 80;
  const centerX = 100;
  const centerY = 100;
  
  const startRad = Math.PI;
  const endRad = Math.PI + (angle * Math.PI) / 180;
  
  const x1 = centerX + radius * Math.cos(startRad);
  const y1 = centerY + radius * Math.sin(startRad);
  const x2 = centerX + radius * Math.cos(endRad);
  const y2 = centerY + radius * Math.sin(endRad);
  
  const largeArcFlag = angle > 180 ? 1 : 0;
  const pathData = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
  
  const getColor = () => {
    if (percentage >= 70) return '#4caf50';
    if (percentage >= 40) return '#ff9800';
    return '#f44336';
  };
  
  return (
    <Box sx={{ textAlign: 'center', p: 2 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#1A237E', mb: 2 }}>
        Investor Conversion Rate
      </Typography>
      
      <Box sx={{ position: 'relative', display: 'inline-block' }}>
        <svg width={220} height={140} viewBox="0 0 220 140">
          {/* Background arc */}
          <path
            d="M 20 100 A 80 80 0 0 1 180 100"
            fill="none"
            stroke="#e0e0e0"
            strokeWidth="15"
            strokeLinecap="round"
          />
          
          {/* Colored arc based on conversion rate */}
          <path
            d={`M 20 100 A 80 80 0 ${angle > 180 ? 1 : 0} 1 ${x2} ${y2}`}
            fill="none"
            stroke={getColor()}
            strokeWidth="15"
            strokeLinecap="round"
          />
          
          {/* Needle */}
          <line
            x1={centerX}
            y1={centerY - 10}
            x2={centerX + (radius - 20) * Math.cos(Math.PI + (percentage / 100) * Math.PI)}
            y2={centerY - 10 + (radius - 20) * Math.sin(Math.PI + (percentage / 100) * Math.PI)}
            stroke="#333"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle cx={centerX} cy={centerY - 10} r="6" fill="#333" />
          
          <text x={centerX} y={centerY + 25} textAnchor="middle" fontSize="24" fontWeight="bold" fill={getColor()}>
            {formatPercent(percentage)}
          </text>
        </svg>
      </Box>
      
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="textSecondary">Potential Investors</Typography>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#ff9800' }}>{formatNumber(potentialCount)}</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="textSecondary">→ Converted →</Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="textSecondary">Registered Investors</Typography>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#4caf50' }}>{formatNumber(investorCount)}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

// Compact Bar Chart Component
const CompactBarChart = ({ title, data, dataKey, color, formatValue }) => {
  const items = Object.keys(data);
  const maxValue = Math.max(...Object.values(data).map(d => d[dataKey] || 0), 0);
  
  if (items.length === 0 || maxValue === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'white', borderRadius: 2 }}>
        <Typography variant="body2" color="textSecondary">No data available</Typography>
      </Box>
    );
  }
  
  const barWidth = 35;
  const gap = 4;
  const totalWidth = items.length * (barWidth + gap);
  const chartHeight = 180;
  
  return (
    <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 2, height: 'auto', minHeight: 320 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', textAlign: 'center', mb: 1.5, fontSize: '0.9rem' }}>{title}</Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'center', overflowX: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: `${gap}px`, width: totalWidth, justifyContent: 'center' }}>
          {items.map((item) => {
            const value = data[item][dataKey] || 0;
            const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
            const barHeight = (percentage / 100) * chartHeight;
            
            return (
              <Box key={item} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: `${barWidth}px`, flexShrink: 0 }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: color, 
                    mb: 0.15,
                    fontSize: '0.6rem',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {formatValue(value)}
                </Typography>
                <Box 
                  sx={{ 
                    width: '100%', 
                    bgcolor: '#e0e0e0', 
                    borderRadius: 0.5, 
                    height: chartHeight,
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <Box 
                    sx={{ 
                      height: `${barHeight}px`, 
                      bgcolor: color, 
                      width: '100%',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      borderRadius: 0.5,
                      transition: 'height 0.3s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }} 
                  />
                </Box>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    mt: 0.5, 
                    textAlign: 'center',
                    fontSize: '0.55rem',
                    fontWeight: 'medium',
                    width: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  title={item}
                >
                  {item.length > 12 ? item.substring(0, 10) + '...' : item}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

// Main Investor Report Component
export default function InvestorReport() {
  // State Management
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedCountry, setSelectedCountry] = useState("all");
  const [selectedSector, setSelectedSector] = useState("all");
  const [selectedPark, setSelectedPark] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [minCapital, setMinCapital] = useState("");
  const [maxCapital, setMaxCapital] = useState("");
  
  // Chart States
  const [showChart, setShowChart] = useState(false);
  const [chartView, setChartView] = useState("overview");
  const [chartTab, setChartTab] = useState(0);
  const [chartType, setChartType] = useState('bar');
  
  // Data States
  const [investors, setInvestors] = useState([]);
  const [potentialInvestors, setPotentialInvestors] = useState([]);
  const [filteredInvestors, setFilteredInvestors] = useState([]);
  const [filteredPotentialInvestors, setFilteredPotentialInvestors] = useState([]);
  
  // Dynamic filter options
  const [countriesList, setCountriesList] = useState(["all"]);
  const [sectorsList, setSectorsList] = useState(["all"]);
  const [parksList, setParksList] = useState(["all"]);
  const [statusesList, setStatusesList] = useState(["all"]);
  
  // Dialog States
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [openPotentialDialog, setOpenPotentialDialog] = useState(false);
  const [selectedPotential, setSelectedPotential] = useState(null);

  const getStatusColor = (status) => {
    const statusUpper = status?.toUpperCase();
    switch(statusUpper) {
      case 'ACTIVE':
      case 'APPROVED':
        return { bg: '#e8f5e9', color: '#2e7d32' };
      case 'PENDING':
      case 'UNDER_REVIEW':
        return { bg: '#fff3e0', color: '#ef6c00' };
      case 'INACTIVE':
      case 'REJECTED':
        return { bg: '#ffebee', color: '#c62828' };
      default: 
        return { bg: '#f5f5f5', color: '#616161' };
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      return format(new Date(date), 'dd/MM/yyyy');
    } catch {
      return "Invalid Date";
    }
  };

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [mockInvestors, mockPotentialInvestors] = await Promise.all([
          GetDataFromApiWithParams(`investors/investorinfo`),
          GetDataFromApiWithParams(`prospectiveInvestors`),
        ]);
        
        setInvestors(mockInvestors);
        setFilteredInvestors(mockInvestors);
        setPotentialInvestors(mockPotentialInvestors);
        setFilteredPotentialInvestors(mockPotentialInvestors);
        
        // Extract unique filter options
        const uniqueCountries = new Set();
        const uniqueSectors = new Set();
        const uniqueParks = new Set();
        const uniqueStatuses = new Set();
        
        mockPotentialInvestors.forEach(pot => {
          if (pot.country_of_origin) uniqueCountries.add(pot.country_of_origin);
          if (pot.sector_of_interest) uniqueSectors.add(pot.sector_of_interest);
          if (pot.park_interested_in) uniqueParks.add(pot.park_interested_in);
        });
        
        mockInvestors.forEach(inv => {
          if (inv.status) uniqueStatuses.add(inv.status);
          if (inv.nationality_origin) uniqueCountries.add(inv.nationality_origin);
        });
        
        setCountriesList(["all", ...Array.from(uniqueCountries).sort()]);
        setSectorsList(["all", ...Array.from(uniqueSectors).sort()]);
        setParksList(["all", ...Array.from(uniqueParks).sort()]);
        setStatusesList(["all", ...Array.from(uniqueStatuses).sort()]);
        
      } catch (error) {
        console.error("Failed to fetch investor data:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const totalInvestors = filteredInvestors.length;
    const totalPotential = filteredPotentialInvestors.length;
    const totalActiveInvestors = filteredInvestors.filter(i => i.status === "ACTIVE" || i.status === null).length;
    const totalPendingInvestors = filteredInvestors.filter(i => i.status === "PENDING").length;
    const totalCapital = filteredInvestors.reduce((sum, i) => sum + safeParseNumber(i.capital), 0);
    const avgCapital = totalInvestors > 0 ? totalCapital / totalInvestors : 0;
    
    // Calculate conversion rate from potential investor to investor
    const convertedCount = filteredPotentialInvestors.filter(potential => 
      filteredInvestors.some(investor => 
        investor.email?.toLowerCase() === potential.email?.toLowerCase() ||
        investor.company_name?.toLowerCase() === potential.investor_company_name?.toLowerCase()
      )
    ).length;
    
    const conversionRate = totalPotential > 0 ? (convertedCount / totalPotential) * 100 : 0;
    
    // Sector distribution from potential investors
    const sectorDistribution = {};
    filteredPotentialInvestors.forEach(pot => {
      const sector = pot.sector_of_interest || "Unknown";
      sectorDistribution[sector] = (sectorDistribution[sector] || 0) + 1;
    });
    
    // Country distribution from investors
    const countryDistribution = {};
    filteredInvestors.forEach(inv => {
      let country = inv.nationality_origin || "Unknown";
      country = country.trim().toLowerCase();
      const aliases = {
        ethiopa: "ethiopia",
        "china /honghong/": "china",
        hongkong: "china",
        "taiwan (china)": "china",
        "sirilanka": "sri lanka",
        "sirilanka/china": "sri lanka",
      };
      country = aliases[country] || country;
      countryDistribution[country] = (countryDistribution[country] || 0) + 1;
    });
    
    // Monthly trends for potential investors
    const monthlyPotential = {
      "Jan": 0, "Feb": 0, "Mar": 0, "Apr": 0, "May": 0, "Jun": 0,
      "Jul": 0, "Aug": 0, "Sep": 0, "Oct": 0, "Nov": 0, "Dec": 0
    };
    
    filteredPotentialInvestors.forEach(pot => {
      if (pot.date_of_visit) {
        const monthNum = new Date(pot.date_of_visit).getMonth();
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        monthlyPotential[monthNames[monthNum]]++;
      }
    });
    
    // Park interest distribution
    const parkDistribution = {};
    filteredPotentialInvestors.forEach(pot => {
      const park = pot.park_interested_in || "Unknown";
      parkDistribution[park] = (parkDistribution[park] || 0) + 1;
    });
    
    // Purpose of visit distribution
    const purposeDistribution = {};
    filteredPotentialInvestors.forEach(pot => {
      const purpose = pot.purpose_of_visit || "Unknown";
      purposeDistribution[purpose] = (purposeDistribution[purpose] || 0) + 1;
    });
    
    // How they know about us distribution
    const sourceDistribution = {};
    filteredPotentialInvestors.forEach(pot => {
      const source = pot.how_you_know_about_us || "Unknown";
      sourceDistribution[source] = (sourceDistribution[source] || 0) + 1;
    });
    
    // Yearly trends
    const yearlyData = {};
    filteredPotentialInvestors.forEach(pot => {
      if (pot.date_of_visit) {
        const year = new Date(pot.date_of_visit).getFullYear().toString();
        if (!yearlyData[year]) {
          yearlyData[year] = { totalPotential: 0, totalConverted: 0, conversionRate: 0 };
        }
        yearlyData[year].totalPotential++;
        
        const isConverted = filteredInvestors.some(inv => 
          inv.email?.toLowerCase() === pot.email?.toLowerCase() ||
          inv.company_name?.toLowerCase() === pot.investor_company_name?.toLowerCase()
        );
        if (isConverted) {
          yearlyData[year].totalConverted++;
        }
      }
    });
    
    Object.keys(yearlyData).forEach(year => {
      yearlyData[year].conversionRate = yearlyData[year].totalPotential > 0 
        ? (yearlyData[year].totalConverted / yearlyData[year].totalPotential) * 100 
        : 0;
    });
    
    // Capital range distribution
    const capitalRanges = {
      "< $1M": 0,
      "$1M - $5M": 0,
      "$5M - $10M": 0,
      "$10M - $20M": 0,
      "> $20M": 0
    };
    
    filteredInvestors.forEach(inv => {
      const capital = safeParseNumber(inv.capital);
      if (capital < 1000000) capitalRanges["< $1M"]++;
      else if (capital < 5000000) capitalRanges["$1M - $5M"]++;
      else if (capital < 10000000) capitalRanges["$5M - $10M"]++;
      else if (capital < 20000000) capitalRanges["$10M - $20M"]++;
      else capitalRanges["> $20M"]++;
    });
    
    return {
      totalInvestors,
      totalPotential,
      totalActiveInvestors,
      totalPendingInvestors,
      totalCapital,
      avgCapital,
      conversionRate,
      convertedCount,
      sectorDistribution,
      countryDistribution,
      monthlyPotential,
      parkDistribution,
      purposeDistribution,
      sourceDistribution,
      yearlyData,
      capitalRanges
    };
  }, [filteredInvestors, filteredPotentialInvestors]);

  // Chart data preparation
  const chartData = useMemo(() => {
    return {
      statusDistribution: {
        "Active": summaryStats.totalActiveInvestors,
        "Pending": summaryStats.totalPendingInvestors,
        "Inactive": summaryStats.totalInvestors - summaryStats.totalActiveInvestors - summaryStats.totalPendingInvestors
      },
      sectorDistribution: summaryStats.sectorDistribution,
      countryDistribution: summaryStats.countryDistribution,
      monthlyPotential: summaryStats.monthlyPotential,
      parkDistribution: summaryStats.parkDistribution,
      purposeDistribution: summaryStats.purposeDistribution,
      sourceDistribution: summaryStats.sourceDistribution,
      capitalRanges: summaryStats.capitalRanges,
      yearlyData: summaryStats.yearlyData
    };
  }, [summaryStats]);

  // Filter Data
  const applyFilters = useCallback(() => {
    let filteredInvestorList = [...investors];
    let filteredPotentialList = [...potentialInvestors];
    
    // Search filter
    if (searchTerm) {
      filteredInvestorList = filteredInvestorList.filter(item =>
        item.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.investor_id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      filteredPotentialList = filteredPotentialList.filter(item =>
        item.investor_company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.representative_names?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Status filter
    if (selectedStatus !== "all") {
      filteredInvestorList = filteredInvestorList.filter(item => 
        item.status?.toUpperCase() === selectedStatus.toUpperCase()
      );
    }
    
    // Country filter
    if (selectedCountry !== "all") {
      filteredInvestorList = filteredInvestorList.filter(item => 
        item.nationality_origin === selectedCountry
      );
      filteredPotentialList = filteredPotentialList.filter(item => 
        item.country_of_origin === selectedCountry
      );
    }
    
    // Sector filter (only for potential investors)
    if (selectedSector !== "all") {
      filteredPotentialList = filteredPotentialList.filter(item => 
        item.sector_of_interest === selectedSector
      );
    }
    
    // Park filter
    if (selectedPark !== "all") {
      filteredPotentialList = filteredPotentialList.filter(item => 
        item.park_interested_in === selectedPark
      );
    }
    
    // Date range filter (from date and to date)
    if (fromDate) {
      const fromDateTime = new Date(fromDate);
      fromDateTime.setHours(0, 0, 0, 0);
      filteredPotentialList = filteredPotentialList.filter(item => {
        if (!item.date_of_visit) return false;
        return new Date(item.date_of_visit) >= fromDateTime;
      });
    }
    
    if (toDate) {
      const toDateTime = new Date(toDate);
      toDateTime.setHours(23, 59, 59, 999);
      filteredPotentialList = filteredPotentialList.filter(item => {
        if (!item.date_of_visit) return false;
        return new Date(item.date_of_visit) <= toDateTime;
      });
    }
    
    // Capital range filter
    if (minCapital !== "") {
      const minVal = safeParseNumber(minCapital);
      filteredInvestorList = filteredInvestorList.filter(item => safeParseNumber(item.capital) >= minVal);
    }
    
    if (maxCapital !== "") {
      const maxVal = safeParseNumber(maxCapital);
      filteredInvestorList = filteredInvestorList.filter(item => safeParseNumber(item.capital) <= maxVal);
    }
    
    setFilteredInvestors(filteredInvestorList);
    setFilteredPotentialInvestors(filteredPotentialList);
    setPage(0);
  }, [investors, potentialInvestors, searchTerm, selectedStatus, selectedCountry, selectedSector, selectedPark, fromDate, toDate, minCapital, maxCapital]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Export to CSV
  const handleExportInvestorsCSV = () => {
    const headers = ['investor_id', 'tin_no', 'company_name', 'contact_person', 'phone_no', 'mobile_no', 'email', 'website', 'nationality_origin', 'description', 'contact_person_position', 'capital', 'status'];
    const data = filteredInvestors.map(item => [
      item.investor_id,
      item.tin_no,
      item.company_name,
      item.contact_person,
      item.phone_no,
      item.mobile_no,
      item.email,
      item.website,
      item.nationality_origin,
      item.description,
      item.contact_person_position,
      item.capital,
      item.status
    ]);
    
    const csvContent = [headers, ...data].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `investors_${format(new Date(), 'yyyyMMdd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  const handleExportPotentialCSV = () => {
    const headers = ['id', 'date_of_visit', 'investor_company_name', 'country_of_origin', 'sector_of_interest', 'representative_names', 'park_interested_in', 'purpose_of_visit', 'how_you_know_about_us', 'email', 'phone_no', 'responsible_ipdc_officer'];
    const data = filteredPotentialInvestors.map(item => [
      item.id,
      formatDate(item.date_of_visit),
      item.investor_company_name,
      item.country_of_origin,
      item.sector_of_interest,
      item.representative_names,
      item.park_interested_in,
      item.purpose_of_visit,
      item.how_you_know_about_us,
      item.email,
      item.phone_no,
      item.responsible_ipdc_officer
    ]);
    
    const csvContent = [headers, ...data].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `potential_investors_${format(new Date(), 'yyyyMMdd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedStatus("all");
    setSelectedCountry("all");
    setSelectedSector("all");
    setSelectedPark("all");
    setFromDate("");
    setToDate("");
    setMinCapital("");
    setMaxCapital("");
  };

  const handleChangePage = (event, newPage) => { setPage(newPage); };
  const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };
  const getCurrentPageData = () => { 
    if (tabValue === 0) {
      return filteredInvestors.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    } else {
      return filteredPotentialInvestors.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }
  };
  
  const handleViewInvestorDetails = (investor) => { 
    setSelectedInvestor(investor); 
    setOpenDetailsDialog(true); 
  };
  
  const handleViewPotentialDetails = (potential) => { 
    setSelectedPotential(potential); 
    setOpenPotentialDialog(true); 
  };

  const renderChart = (title, data, dataKey, color, formatValue) => {
    const hasValidData = Object.values(data).some(item => {
      const value = typeof item === 'object' ? item[dataKey] : item;
      return value && value > 0;
    });
    
    if (!hasValidData) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">No data available for {title}</Typography>
        </Box>
      );
    }
    
    if (chartType === 'pie') {
      const pieData = {};
      Object.entries(data).forEach(([key, val]) => { 
        const value = typeof val === 'object' ? val[dataKey] : val;
        if (value > 0) {
          pieData[key] = value;
        }
      });
      
      if (Object.keys(pieData).length === 0) {
        return (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary">No data available for {title}</Typography>
          </Box>
        );
      }
      return <PieChartComponent title={title} data={pieData} formatValue={formatValue} />;
    } else {
      const barData = {};
      Object.entries(data).forEach(([key, val]) => {
        barData[key] = { [dataKey]: typeof val === 'object' ? val[dataKey] : val };
      });
      return <CompactBarChart title={title} data={barData} dataKey={dataKey} color={color} formatValue={formatValue} />;
    }
  };
  
  return (
    <Box sx={{ width: '100%', bgcolor: '#f5f5f5', minHeight: '100vh', p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ color: '#1a237e', fontWeight: 'bold', textAlign: 'center', mb: 1 }}>
        Investor Relations Dashboard
      </Typography>
    
      
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <BusinessIcon sx={{ color: '#1976d2', fontSize: 28, mb: 1 }} />
              <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                {formatNumber(summaryStats.totalInvestors)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>Total Investors</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUpIcon sx={{ color: '#ff9800', fontSize: 28, mb: 1 }} />
              <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                {formatNumber(summaryStats.totalPotential)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>Potential Investors</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 28, mb: 1 }} />
              <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                {formatNumber(summaryStats.totalActiveInvestors)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>Active Investors</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <CapitalIcon sx={{ color: '#9c27b0', fontSize: 28, mb: 1 }} />
              <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                {formatCurrency(summaryStats.totalCapital)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>Total Capital</Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: 'white', boxShadow: 1, height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <PercentIcon sx={{ color: '#e91e63', fontSize: 28, mb: 1 }} />
              <Typography variant="h6" sx={{ color: '#1a237e', fontWeight: 'bold' }}>
                {formatPercent(summaryStats.conversionRate)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>Conversion Rate</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
     
      
      {/* Filters Section */}
      <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>),
                endAdornment: searchTerm && (<InputAdornment position="end"><ClearIcon sx={{ cursor: 'pointer', fontSize: 'small' }} onClick={() => setSearchTerm("")} /></InputAdornment>)
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Park</InputLabel>
              <Select value={selectedPark} onChange={(e) => setSelectedPark(e.target.value)} label="Park">
                {parksList.map((park) => (<MenuItem key={park} value={park}>{park === "all" ? "All Parks" : park}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Sector</InputLabel>
              <Select value={selectedSector} onChange={(e) => setSelectedSector(e.target.value)} label="Sector">
                {sectorsList.map((sector) => (<MenuItem key={sector} value={sector}>{sector === "all" ? "All Sectors" : sector}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} label="Status">
                {statusesList.map((status) => (<MenuItem key={status} value={status}>{status === "all" ? "All Status" : status}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Country</InputLabel>
              <Select value={selectedCountry} onChange={(e) => setSelectedCountry(e.target.value)} label="Country">
                {countriesList.map((country) => (<MenuItem key={country} value={country}>{country === "all" ? "All Countries" : country}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={1.5}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From Date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12} md={1.5}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To Date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
        
        <Grid container spacing={2} alignItems="center" justifyContent="flex-end">
          <Grid item>
            <Button variant="outlined" onClick={resetFilters} startIcon={<ClearIcon />} size="small" sx={{ textTransform: 'none' }}>
              Reset Filters
            </Button>
          </Grid>
          <Grid item>
            <Button variant="contained" color="primary" size="small" onClick={handleExportInvestorsCSV} startIcon={<DownloadIcon />} sx={{ textTransform: 'none' }}>
              Export Investors
            </Button>
          </Grid>
          <Grid item>
            <Button variant="outlined" color="secondary" size="small" onClick={handleExportPotentialCSV} startIcon={<DownloadIcon />} sx={{ textTransform: 'none' }}>
              Export Potential
            </Button>
          </Grid>
          <Grid item>
            <Button variant="outlined" size="small" onClick={() => setShowChart(!showChart)} sx={{ textTransform: 'none' }}>
              {showChart ? "Hide Chart" : "Show Chart"}
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Tabs for Investors and Potential Investors */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#fafafa' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label={`Investors (${filteredInvestors.length})`} />
            <Tab label={`Potential Investors (${filteredPotentialInvestors.length})`} />
          </Tabs>
        </Box>
        
        {!showChart && (
          <>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading investor data...</Typography>
              </Box>
            ) : (tabValue === 0 ? filteredInvestors.length === 0 : filteredPotentialInvestors.length === 0) ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <Typography sx={{ color: 'text.secondary' }}>No data available</Typography>
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                      <TableRow>
                        {tabValue === 0 ? (
                          <>
                            <TableCell sx={{ fontWeight: 'bold' }}>Company Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Contact Person</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Nationality</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Capital ($)</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell sx={{ fontWeight: 'bold' }}>Company Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Representative</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Country</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Sector</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Park Interested</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Visit Date</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                          </>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getCurrentPageData().map((row) => (
                        <TableRow key={row.id || row.investor_id} hover>
                          {tabValue === 0 ? (
                            <>
                              <TableCell>{row.company_name}</TableCell>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2">{row.contact_person}</Typography>
                                  <Typography variant="caption" color="textSecondary">{row.contact_person_position}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <EmailIcon sx={{ fontSize: 12, color: '#666' }} />
                                  <Typography variant="body2">{row.email}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <LocationOnIcon sx={{ fontSize: 12, color: '#666' }} />
                                  {row.nationality_origin}
                                </Box>
                              </TableCell>
                              <TableCell>{formatCurrency(row.capital)}</TableCell>
                              <TableCell>
                              {row.status} 
                                
                              </TableCell>
                              <TableCell>
                                <Tooltip title="View Details">
                                  <IconButton size="small" onClick={() => handleViewInvestorDetails(row)}>
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell>{row.investor_company_name}</TableCell>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2">{row.representative_names}</Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                    <PhoneIcon sx={{ fontSize: 10, color: '#666' }} />
                                    <Typography variant="caption">{row.phone_no}</Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>{row.country_of_origin}</TableCell>
                              <TableCell>
                              {row.sector_of_interest}
                               
                              </TableCell>
                              <TableCell>{row.park_interested_in}</TableCell>
                              <TableCell>{formatDate(row.date_of_visit)}</TableCell>
                              <TableCell>
                                <Tooltip title="View Details">
                                  <IconButton size="small" onClick={() => handleViewPotentialDetails(row)}>
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination 
                  rowsPerPageOptions={[5, 10, 25, 50]} 
                  component="div" 
                  count={tabValue === 0 ? filteredInvestors.length : filteredPotentialInvestors.length} 
                  rowsPerPage={rowsPerPage} 
                  page={page} 
                  onPageChange={handleChangePage} 
                  onRowsPerPageChange={handleChangeRowsPerPage} 
                />
              </>
            )}
          </>
        )}
        
        {/* Charts Section */}
        {showChart && (
          <Box sx={{ p: 2 }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button variant={chartView === 'overview' ? 'contained' : 'outlined'} size="small" onClick={() => setChartView('overview')} sx={{ textTransform: 'none', minWidth: '120px' }}>
                <BarChartIcon sx={{ mr: 1, fontSize: 18 }} />Overview
              </Button>
              <Button variant={chartView === 'yearly' ? 'contained' : 'outlined'} size="small" onClick={() => setChartView('yearly')} sx={{ textTransform: 'none', minWidth: '120px' }}>
                <TimelineIcon sx={{ mr: 1, fontSize: 18 }} />Yearly Trend
              </Button>
            </Box>
            
            <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
              {chartView === 'overview' ? (
                <>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="textSecondary">Chart Type:</Typography>
                      <ToggleButtonGroup value={chartType} exclusive onChange={(e, val) => val && setChartType(val)} size="small">
                        <ToggleButton value="bar">
                          <Tooltip title="Bar Chart">
                            <BarChartIcon fontSize="small" />
                          </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="pie">
                          <Tooltip title="Pie Chart">
                            <PieChartIcon fontSize="small" />
                          </Tooltip>
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  </Box>
                  
                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tabs value={chartTab} onChange={(e, newValue) => setChartTab(newValue)} variant="scrollable" scrollButtons="auto">
                      <Tab label="Status Distribution" />
                      <Tab label="Sector of Interest" />
                      <Tab label="Country Distribution" />
                      <Tab label="Monthly Visits" />
                      <Tab label="Park Interest" />
                      <Tab label="Purpose of Visit" />
                      <Tab label="Source/Referral" />
                      <Tab label="Capital Range" />
                    </Tabs>
                  </Box>
                  
                  <Box sx={{ mt: 2, minHeight: 450 }}>
                    <TabPanel value={chartTab} index={0}>
                      {renderChart("Investor Status Distribution", chartData.statusDistribution, "value", "#1976d2", formatNumber)}
                    </TabPanel>
                    <TabPanel value={chartTab} index={1}>
                      {renderChart("Sector of Interest", chartData.sectorDistribution, "value", "#ff9800", formatNumber)}
                    </TabPanel>
                    <TabPanel value={chartTab} index={2}>
                      {renderChart("Country Distribution", chartData.countryDistribution, "value", "#4caf50", formatNumber)}
                    </TabPanel>
                    <TabPanel value={chartTab} index={3}>
                      {renderChart("Monthly Visit Trends", chartData.monthlyPotential, "value", "#e91e63", formatNumber)}
                    </TabPanel>
                    <TabPanel value={chartTab} index={4}>
                      {renderChart("Park Interest", chartData.parkDistribution, "value", "#9c27b0", formatNumber)}
                    </TabPanel>
                    <TabPanel value={chartTab} index={5}>
                      {renderChart("Purpose of Visit", chartData.purposeDistribution, "value", "#00bcd4", formatNumber)}
                    </TabPanel>
                    <TabPanel value={chartTab} index={6}>
                      {renderChart("How They Know About Us", chartData.sourceDistribution, "value", "#795548", formatNumber)}
                    </TabPanel>
                    <TabPanel value={chartTab} index={7}>
                      {renderChart("Capital Range Distribution", chartData.capitalRanges, "value", "#3f51b5", formatNumber)}
                    </TabPanel>
                  </Box>
                </>
              ) : (
                <YearlyTrendLineChart 
                  title="Yearly Investor Conversion Trends" 
                  yearlyData={chartData.yearlyData} 
                  dataKeys={['totalPotential', 'totalConverted', 'conversionRate']}
                  colors={['#ff9800', '#4caf50', '#e91e63']}
                  formatValue={(v) => typeof v === 'number' && v < 1 && v > 0 ? formatPercent(v) : formatNumber(v)}
                />
              )}
            </Paper>
          </Box>
        )}
      </Paper>
      
      {/* Investor Details Dialog */}
      <Dialog open={openDetailsDialog} onClose={() => setOpenDetailsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
              Investor Details
            </Typography>
            <IconButton onClick={() => setOpenDetailsDialog(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {selectedInvestor && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Investor ID</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>{selectedInvestor.investor_id}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>TIN No</Typography>
                  <Typography variant="body1">{selectedInvestor.tin_no}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Company Name</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{selectedInvestor.company_name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Status</Typography>
                  <Chip label={selectedInvestor.status} size="small" sx={getStatusColor(selectedInvestor.status)} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Contact Person</Typography>
                  <Typography variant="body1">{selectedInvestor.contact_person}</Typography>
                  <Typography variant="caption" color="textSecondary">{selectedInvestor.contact_person_position}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Contact Numbers</Typography>
                  <Typography variant="body2">Phone: {selectedInvestor.phone_no}</Typography>
                  <Typography variant="body2">Mobile: {selectedInvestor.mobile_no}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Email</Typography>
                  <Typography variant="body1">{selectedInvestor.email}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Website</Typography>
                  <Typography variant="body1">{selectedInvestor.website || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Nationality</Typography>
                  <Typography variant="body1">{selectedInvestor.nationality_origin}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Capital</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>{formatCurrency(selectedInvestor.capital)}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Description</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#f5f5f5', mt: 0.5 }}>
                    <Typography variant="body2">{selectedInvestor.description || 'No description provided'}</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={() => setOpenDetailsDialog(false)} variant="outlined">Close</Button>
        </Box>
      </Dialog>
      
      {/* Potential Investor Details Dialog */}
      <Dialog open={openPotentialDialog} onClose={() => setOpenPotentialDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
              Potential Investor Details
            </Typography>
            <IconButton onClick={() => setOpenPotentialDialog(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {selectedPotential && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Company Name</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{selectedPotential.investor_company_name}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Date of Visit</Typography>
                  <Typography variant="body1">{formatDate(selectedPotential.date_of_visit)}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Representative</Typography>
                  <Typography variant="body1">{selectedPotential.representative_names}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Country of Origin</Typography>
                  <Typography variant="body1">{selectedPotential.country_of_origin}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Sector of Interest</Typography>
                  <Chip label={selectedPotential.sector_of_interest} size="small" color="primary" variant="outlined" />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Park Interested In</Typography>
                  <Typography variant="body1">{selectedPotential.park_interested_in}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Contact Email</Typography>
                  <Typography variant="body1">{selectedPotential.email}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Contact Phone</Typography>
                  <Typography variant="body1">{selectedPotential.phone_no}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Purpose of Visit</Typography>
                  <Typography variant="body2">{selectedPotential.purpose_of_visit}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>How They Know About Us</Typography>
                  <Typography variant="body2">{selectedPotential.how_you_know_about_us}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Responsible IPDC Officer</Typography>
                  <Typography variant="body1">{selectedPotential.responsible_ipdc_officer}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ color: '#666' }}>Created At</Typography>
                  <Typography variant="caption">{formatDate(selectedPotential.created_at)}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={() => setOpenPotentialDialog(false)} variant="outlined">Close</Button>
        </Box>
      </Dialog>
    </Box>
  );
}